import { useState, useEffect } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  Plane, LogOut, LayoutDashboard, Route, Users, Sparkles, Building2, Star,
  Inbox, ChevronsLeft, ChevronsRight
} from "lucide-react";

export default function DashboardLayout({ children, role }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem("as_sidebar_collapsed") === "1"; } catch { return false; }
  });

  useEffect(() => {
    try { localStorage.setItem("as_sidebar_collapsed", collapsed ? "1" : "0"); } catch {}
  }, [collapsed]);

  const opsItems = [
    { to: "/ops", label: "Dashboard", icon: LayoutDashboard, end: true },
    { to: "/ops/trips", label: "Trips", icon: Route },
    { to: "/ops/clients", label: "Clients", icon: Users },
    { to: "/ops/concierge", label: "Concierge", icon: Sparkles },
    { to: "/ops/suppliers", label: "Suppliers", icon: Building2 },
    { to: "/ops/fleet", label: "Fleet", icon: Plane },
    { to: "/ops/inquiries", label: "Charter Inquiries", icon: Inbox },
  ];

  const clientItems = [
    { to: "/portal", label: "Overview", icon: LayoutDashboard, end: true },
    { to: "/portal/trips", label: "My Trips", icon: Route },
    { to: "/portal/concierge", label: "Concierge", icon: Sparkles },
    { to: "/portal/profile", label: "Preferences", icon: Star },
  ];

  const items = role === "client" ? clientItems : opsItems;
  const width = collapsed ? "w-[68px]" : "w-[240px]";

  const initials = (user?.name || "").split(" ").map(s => s[0]).slice(0, 2).join("").toUpperCase() || "AS";

  return (
    <div className="min-h-screen flex bg-[#FAF7F0] text-[#1A1D24]">
      {/* Sidebar */}
      <aside
        className={`${width} shrink-0 border-r border-[#E5DFD2] bg-white flex flex-col sticky top-0 h-screen transition-[width] duration-200 ease-out`}
        data-testid="dashboard-sidebar"
        data-collapsed={collapsed ? "true" : "false"}
      >
        {/* Brand + toggle */}
        <div className={`h-16 flex items-center border-b border-[#E5DFD2] ${collapsed ? "justify-center px-2" : "justify-between px-5"}`}>
          {collapsed ? (
            <button
              onClick={() => setCollapsed(false)}
              className="p-2 rounded-sm text-[#756E64] hover:text-[#A47E2A] hover:bg-[#F1EDE4] transition-colors"
              data-testid="sidebar-expand"
              aria-label="Expand sidebar"
            >
              <ChevronsRight size={16} />
            </button>
          ) : (
            <>
              <Link to="/" className="flex items-center gap-2" data-testid="sidebar-brand">
                <Plane size={18} className="as-gold" strokeWidth={1.75}/>
                <span className="text-base font-semibold tracking-tight">AeroSphere</span>
              </Link>
              <button
                onClick={() => setCollapsed(true)}
                className="p-1.5 rounded-sm text-[#756E64] hover:text-[#A47E2A] hover:bg-[#F1EDE4] transition-colors"
                data-testid="sidebar-collapse"
                aria-label="Collapse sidebar"
              >
                <ChevronsLeft size={16} />
              </button>
            </>
          )}
        </div>

        {/* Section label */}
        {!collapsed && (
          <div className="px-4 pt-6 pb-3">
            <div className="as-overline">{role === "client" ? "Client Portal" : "Operations"}</div>
          </div>
        )}
        {collapsed && <div className="pt-5" />}

        {/* Nav */}
        <nav className={`flex-1 ${collapsed ? "px-2" : "px-3"} space-y-1 overflow-y-auto`}>
          {items.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              end={it.end}
              title={collapsed ? it.label : undefined}
              data-testid={`nav-${it.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center ${collapsed ? "justify-center" : "gap-3 px-3"} py-2.5 rounded-sm text-sm transition-colors ${
                  isActive
                    ? "bg-[#F1EDE4] text-[#1A1D24] border-l-2 border-[#A47E2A]"
                    : "text-[#756E64] hover:bg-[#F1EDE4] hover:text-[#1A1D24]"
                }`
              }
            >
              <it.icon size={17} strokeWidth={1.75}/>
              {!collapsed && <span>{it.label}</span>}
            </NavLink>
          ))}
        </nav>

        {/* Sticky user block */}
        <div
          className={`border-t border-[#E5DFD2] bg-white sticky bottom-0 ${collapsed ? "px-2 py-3" : "px-4 py-4"}`}
          data-testid="sidebar-user"
        >
          {collapsed ? (
            <div className="flex flex-col items-center gap-2">
              <div
                title={`${user?.name} · ${user?.role}`}
                className="w-9 h-9 rounded-full bg-[#A47E2A] text-white flex items-center justify-center text-xs font-semibold"
                data-testid="sidebar-user-initials"
              >
                {initials}
              </div>
              <button
                onClick={async () => { await logout(); nav("/"); }}
                title="Sign out"
                aria-label="Sign out"
                className="p-2 rounded-sm text-[#756E64] hover:text-red-600 hover:bg-red-50 transition-colors"
                data-testid="sidebar-logout"
              >
                <LogOut size={15}/>
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-[#A47E2A] text-white flex items-center justify-center text-xs font-semibold shrink-0">
                {initials}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm text-[#1A1D24] leading-tight truncate" data-testid="sidebar-user-name">{user?.name}</div>
                <div className="as-mono text-[10px] text-[#756E64] uppercase tracking-widest mt-0.5" data-testid="sidebar-user-role">{user?.role}</div>
              </div>
              <button
                onClick={async () => { await logout(); nav("/"); }}
                title="Sign out"
                aria-label="Sign out"
                className="p-2 rounded-sm text-[#756E64] hover:text-red-600 hover:bg-red-50 transition-colors"
                data-testid="sidebar-logout"
              >
                <LogOut size={15}/>
              </button>
            </div>
          )}
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0 overflow-x-hidden" data-testid="dashboard-main">
        {children}
      </main>
    </div>
  );
}
